import { useEffect, useState, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import {
  UsersThree,
  Building,
  Eye,
  Globe,
  House,
  PaperPlaneTilt,
  CalendarDotIcon,
  CalendarSlashIcon,
  ClockIcon,
} from "@phosphor-icons/react";
import { Chip, Tooltip } from "@heroui/react";
import { ArrowUpRight } from "assets/icons";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { DashboardBlock, DashboardBlockHeader, DashboardBlockContent } from "../layout/DashboardBlock";
import { BlockLoadingState, BlockEmptyState } from "../states/BlockStates";
import { blockStyles } from "../utils/blockStyles.utils";
import { statusTranslation } from "components/candidates/types";
import { InvitedSource } from "shared/types/rental-candidate";
import { CandidateEmailModal } from "components/candidates/email";
import { formatCandidateDate, toCandidateDate } from "../utils/candidateDate.utils";

interface CandidateCardProps {
  application: any;
}

export const CandidatesBlockNew = () => {
  const { session } = useSession();
  const [applications, setApplications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedData = useRef(false);

  useEffect(() => {
    if (!session || hasLoadedData.current) {
      return;
    }

    const fetchCandidates = async () => {
      setIsLoading(true);
      try {
        // excludeClosed: tenant / rejected / not-interested applications are not
        // candidates anymore. It also keeps the synthetic "legacy tenant" rows
        // (no appointment, no createDate) out of this block.
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/get-appointments?excludeClosed=true`,
          { method: "GET" },
          session
        );

        if (!response.ok) {
          console.error("Failed to fetch candidates:", response.status, response.statusText);
          setApplications([]);
          return;
        }

        const data = await response.json();
        
        if (!Array.isArray(data)) {
          console.error("Expected array but got:", data);
          setApplications([]);
          return;
        }

        setApplications(data.map((candidate: any) => ({
          ...candidate,
          // Never store an Invalid Date: date-fns throws on it at render time.
          createDate: toCandidateDate(candidate.createDate),
          visitDate: toCandidateDate(candidate.visitDate),
          moveDate: {
            agreement: candidate.moveDate.agreement,
            explanation: candidate.moveDate.explanation ? new Date(candidate.moveDate.explanation) : null
          },
          residents: candidate.residents?.map((resident: any) => ({
            ...resident,
            dateOfBirth: new Date(resident.dateOfBirth),
          })) || []
        })));
      } catch (error) {
        console.error("Error fetching candidates:", error);
        setApplications([]);
      } finally {
        setIsLoading(false);
        hasLoadedData.current = true;
      }
    };
    fetchCandidates();
  }, [session?.id]);

  const CandidateCard: React.FC<CandidateCardProps> = ({ application }) => {
    const navigate = useNavigate();
    const [status, Icon, statusClass] = statusTranslation(application.status);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const hasVisitDate = toCandidateDate(application.visitDate) !== null;

    const handleManageCandidateClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowEmailModal(true);
    };

    const handleEmailSuccess = () => {
      setShowEmailModal(false);
      window.location.reload();
    };

    return (
      <>
        <div className="flex w-full cursor-pointer flex-col gap-2.5 p-3 hover:bg-moss-primary/5 transition-colors border-b border-neutral-primary/5 last:border-b-0">
          <div className="flex w-full items-start justify-between gap-2">
            {/* Left side - Candidate info */}
            <div className="flex flex-col gap-1.5 grow min-w-0">
              <p className="font-semibold text-foreground text-sm">
                {application.personalDetails.firstName} {application.personalDetails.lastName}
              </p>
              
              {/* Visit date status */}
              <div className={`flex items-center gap-1.5 text-xs ${hasVisitDate ? 'text-moss-primary' : 'text-brick-primary'}`}>
                {hasVisitDate ? (
                  <>
                    <CalendarDotIcon weight="duotone" size={14} />
                    <span>Afspraak op {formatCandidateDate(application.visitDate, { candidateId: application.id, field: 'visitDate' })}</span>
                  </>
                ) : (
                  <>
                    <CalendarSlashIcon weight="duotone" size={14} />
                    <span>Nog geen afspraak ingepland</span>
                  </>
                )}
              </div>

              {/* Created date */}
              <div className="flex items-center gap-1.5 text-xs text-neutral-secondary-mid">
                <ClockIcon weight="duotone" size={14} />
                <span>Nieuw in lijst sinds {formatCandidateDate(application.createDate, { candidateId: application.id, field: 'createDate' })}</span>
              </div>

              {/* Status and Source chips */}
              <div className="flex items-center gap-2 flex-wrap">
                <Chip
                  size="sm"
                  startContent={<Icon className={`h-3 w-3 ${statusClass}`} />}
                  classNames={{
                    base: `h-fit bg-muted border border-muted-foreground/50 px-1.5 py-0.5 ${statusClass}`,
                    content: "px-0 font-medium text-xs",
                  }}
                >
                  {status}
                </Chip>
                {application.source && (
                  <Chip
                    size="sm"
                    startContent={
                      application.source === InvitedSource.IMMOWEB ?
                        <Globe weight="duotone" size={12} /> :
                        <House weight="duotone" size={12} />
                    }
                    classNames={{
                      base: `h-fit bg-muted border border-muted-foreground/50 px-1.5 py-0.5 ${application.source === InvitedSource.IMMOWEB ? 'text-brick-primary' : 'text-muted-foreground'}`,
                      content: "px-0 font-medium text-xs",
                    }}
                  >
                    {application.source}
                  </Chip>
                )}
              </div>
            </div>

            {/* Right side - Unit and actions */}
            <div className="flex items-start gap-2 shrink-0">
              <Chip
                size="sm"
                startContent={<Building weight="duotone" size={14} />}
                classNames={{
                  base: "h-fit bg-moss-primary/10 border border-moss-primary text-moss-primary px-1.5 py-0.5",
                  content: "px-0 font-medium text-xs",
                }}
              >
                {application.unitId}
              </Chip>
              <div className="flex items-center gap-1.5">
                <Tooltip content="Kandidaat beheren">
                  <button
                    onClick={handleManageCandidateClick}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <PaperPlaneTilt weight="duotone" size={24} className="text-blue-600" />
                  </button>
                </Tooltip>
                <Tooltip content="Meer details">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/building/${application.buildingId}#verhuur`, { state: { application } });
                    }}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <Eye weight="duotone" size={24} className="text-moss-primary" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        <CandidateEmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSuccess={handleEmailSuccess}
          candidate={application}
        />
      </>
    );
  };

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<UsersThree weight="duotone" size={20} />}
        title="Kandidaten"
        action={
          <a
            href="/rental/candidacies"
            className={blockStyles.link.variants.primary}
          >
            <span className={blockStyles.link.withIcon}>
              Ga naar kandidaten <ArrowUpRight className="h-4 w-4" />
            </span>
          </a>
        }
      />
      
      {isLoading ? (
        <DashboardBlockContent padding="none">
          <BlockLoadingState message="Kandidaten aan het ophalen..." />
        </DashboardBlockContent>
      ) : applications.length > 0 ? (
        <DashboardBlockContent padding="none" className="relative overflow-hidden">
          <div className="relative z-1 flex flex-col">
            {applications.slice(0, 5).map((application: any) => (
              <CandidateCard key={application.id} application={application} />
            ))}
          </div>
          {applications.length > 5 && (
            <div className="text-center p-3 border-t border-neutral-primary/10">
              <a 
                href="/rental/candidacies"
                className={blockStyles.link.variants.primary}
              >
                + {applications.length - 5} meer kandidaten bekijken →
              </a>
            </div>
          )}
          <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-moss-primary/5 pointer-events-none">
            <UsersThree weight="duotone" size={120} />
          </div>
        </DashboardBlockContent>
      ) : (
        <DashboardBlockContent padding="none">
          <BlockEmptyState
            icon={<UsersThree weight="duotone" size={48} />}
            title="Geen kandidaten gevonden"
            description="Er zijn momenteel geen kandidaat-afspraken gepland"
          />
        </DashboardBlockContent>
      )}
    </DashboardBlock>
  );
};
